/**
 * What counts as a "deliverable" file — the single definition shared by every
 * surface that decides whether a file is worth showing a user.
 *
 * An agent workspace is a real filesystem. A coding task touches source files,
 * lockfiles, build output and dependency trees; a document task produces one
 * PDF. Only the second is something the user asked for. Surfaces that blur the
 * two either bury the deliverable (chat chips listing every file a coding
 * agent wrote — #1543) or flood a list with vendor READMEs (the Library).
 *
 * This lives in `@taqat/core` rather than next to either consumer on purpose:
 * the Library lists files server-side and the chat chips are computed
 * client-side, and two copies of "what is a document" would drift until the
 * two surfaces disagreed about the same file.
 *
 * @module types/deliverable-files
 */

/**
 * Extensions a file must have to be shown as a deliverable.
 *
 * An allowlist, not a denylist of code extensions: new languages appear
 * constantly and a missed one would leak source files back into chat, whereas
 * a missed document type merely stays in the file browser until added here.
 */
export const DELIVERABLE_EXTENSIONS: ReadonlySet<string> = new Set([
  // documents
  'pdf',
  'doc',
  'docx',
  'rtf',
  'odt',
  'md',
  'txt',
  // presentations
  'ppt',
  'pptx',
  'odp',
  // spreadsheets / data the user reads directly
  'xls',
  'xlsx',
  'ods',
  'csv',
  // images
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  // bundles
  'zip',
]);

/**
 * Path segments that disqualify a file regardless of extension.
 *
 * Carries more weight than the allowlist: every npm package ships a
 * `README.md` and many ship `.png` assets, so one `bun install` inside a
 * workspace would otherwise produce thousands of "deliverables".
 */
export const NON_DELIVERABLE_SEGMENTS: ReadonlySet<string> = new Set([
  'node_modules',
  '.git',
  '.cache',
  '.venv',
  'venv',
  '__pycache__',
  'dist',
  'build',
  'out',
  '.next',
  '.turbo',
  'vendor',
  'site-packages',
  'coverage',
  'target',
]);

/** Lowercase extension without the dot, or `''` when the file has none. */
export function fileExtension(filePath: string): string {
  const base = filePath.split(/[\\/]/).pop() ?? filePath;
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

/**
 * Whether a workspace file should be surfaced to the user as a deliverable.
 *
 * Rejects, in order: anything under an excluded directory, anything hidden or
 * inside a hidden directory (this is what keeps `.env`, `.npmrc` and other
 * credential-bearing dotfiles from ever being rendered as a clickable
 * attachment), then anything whose extension isn't a document type.
 */
export function isDeliverableFile(filePath: string): boolean {
  if (!filePath) return false;
  const segments = filePath.split(/[\\/]/).filter(Boolean);
  if (segments.length === 0) return false;

  for (const segment of segments) {
    if (NON_DELIVERABLE_SEGMENTS.has(segment)) return false;
    // Leading dot = tooling or secrets, at any depth. `.env` is the case that
    // matters most: chipping it into a conversation would put credentials one
    // click from anyone who can read the chat.
    if (segment.startsWith('.')) return false;
  }

  return DELIVERABLE_EXTENSIONS.has(fileExtension(filePath));
}
